/**
 * Decides how an absolutely-positioned dropdown should be placed horizontally so it never gets
 * clipped by the viewport edges.
 *
 * Preference order:
 *  1. `start`  — keep the default left anchor when the dropdown fits.
 *  2. `end`    — flip to a right anchor when the left anchor overflows but the right anchor fits.
 *  3. `center` — when it fits on neither side (the dropdown is wider than the space available on
 *                both sides), center it on the viewport instead of shoving it against an edge.
 *
 * The decision is computed purely from the trigger's geometry so it is stable and never flip-flops.
 *
 * @param {Object} params
 * @param {number} params.triggerLeft - Trigger's left edge in viewport coordinates.
 * @param {number} params.triggerRight - Trigger's right edge in viewport coordinates.
 * @param {number} params.width - Rendered dropdown width in pixels.
 * @param {number} params.viewportWidth - Viewport width in pixels.
 * @param {number} [params.margin=8] - Minimum gap to keep between the dropdown and the viewport edges.
 * @returns {{mode: 'start'} | {mode: 'end'} | {mode: 'center', left: number}} Placement descriptor.
 *   For `center`, `left` is the CSS `left` offset relative to the trigger (offsetParent) that lands
 *   the dropdown centered on the viewport.
 */
export function resolveDropdownPlacement({ triggerLeft, triggerRight, width, viewportWidth, margin = 8 }) {
    const overflowsRightWhenStartAnchored = triggerLeft + width > viewportWidth - margin;
    if (!overflowsRightWhenStartAnchored) {
        return { mode: 'start' };
    }

    const fitsWhenEndAnchored = triggerRight - width >= margin;
    if (fitsWhenEndAnchored) {
        return { mode: 'end' };
    }

    const viewportCenteredLeft = (viewportWidth - width) / 2;
    return { mode: 'center', left: viewportCenteredLeft - triggerLeft };
}
